import { useMsal } from "@azure/msal-react";
import { ActionIcon, Autocomplete } from "@mantine/core";
import { IconSearch, IconX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { AssetProfile } from "./services/assets/assetProfiles";
import {
    buildLevelOptions,
    buildStageSuggestions,
    isAssetSearchField,
    resolveStageSearch,
    type StageSearchHit,
} from "./services/assets/buildAssetSearchOptions";
import { assetsQueryOptions } from "./services/assets/fetchAssets";
import { fetchFacets, OVERLAY_MAX_RESULT } from "./services/fetchFacets";
import { SUB_FACETS_STALE_TIME } from "./services/subFacetsQuery";
import type { BucketSelection, SelectableField } from "./types";

const searchPlaceholder = ({
    profile,
    fields,
    scope,
}: {
    profile: AssetProfile;
    fields: SelectableField[];
    scope?: BucketSelection;
}): string => {
    if (scope) return `Search machines in ${scope.value}`;
    const levelHint = fields
        .slice(0, 2)
        .map((field) => field.label)
        .join(", ");
    return levelHint ? `Search ${levelHint}… or a machine` : `Enter a name or ${profile.keyLabel}`;
};

export const AssetSearch = ({
    index,
    profile,
    fields,
    selected,
    focusedAssetId,
    onPick,
    onClear,
}: {
    index: string;
    profile: AssetProfile;
    fields: SelectableField[];
    selected?: BucketSelection;
    focusedAssetId?: string;
    onPick: (hit: StageSearchHit) => void;
    onClear: () => void;
}) => {
    const { instance } = useMsal();

    const isSearchSelection =
        Boolean(selected) && isAssetSearchField({ profile, field: selected?.field ?? "" });
    const scope = selected && !isSearchSelection ? selected : undefined;

    const assetsQuery = useQuery({
        ...assetsQueryOptions({ instance, index, profile, selected: scope }),
        enabled: Boolean(index),
        refetchOnWindowFocus: false,
    });
    const assets = useMemo(() => assetsQuery.data ?? [], [assetsQuery.data]);

    const rawValue = focusedAssetId ?? (isSearchSelection ? (selected?.value ?? "") : "");
    const shownValue = useMemo(
        () => assets.find((asset) => asset.id === rawValue)?.label ?? rawValue,
        [assets, rawValue],
    );

    const [text, setText] = useState(shownValue);

    useEffect(() => {
        setText(shownValue);
    }, [shownValue]);

    const facetsQuery = useQuery({
        queryKey: ["overview-categories", index],
        enabled: Boolean(index),
        staleTime: SUB_FACETS_STALE_TIME,
        refetchOnWindowFocus: false,
        queryFn: () => fetchFacets({ instance, index, filters: [], maxResult: OVERLAY_MAX_RESULT }),
    });

    const levels = useMemo(
        () => buildLevelOptions({ facets: facetsQuery.data?.fields, fields }),
        [facetsQuery.data, fields],
    );

    const suggestions = useMemo(
        () => buildStageSuggestions({ profile, assets, levels }),
        [profile, assets, levels],
    );

    const submit = (value: string) => {
        const resolved = resolveStageSearch({ assets, levels, text: value });
        if (resolved) onPick(resolved);
    };

    const clear = () => {
        setText("");
        onClear();
    };

    return (
        <Autocomplete
            data={suggestions}
            value={text}
            onChange={setText}
            onOptionSubmit={submit}
            onKeyDown={(event) => {
                if (event.key === "Enter") submit(text);
            }}
            placeholder={searchPlaceholder({ profile, fields, scope })}
            leftSection={<IconSearch size={15} />}
            rightSection={
                text ? (
                    <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="gray"
                        aria-label="Clear unit search"
                        onClick={clear}
                    >
                        <IconX size={14} />
                    </ActionIcon>
                ) : undefined
            }
            w={300}
            size="sm"
            limit={50}
            styles={{ dropdown: { maxHeight: 280, overflowY: "auto" } }}
        />
    );
};
